import { createSlice } from '@reduxjs/toolkit';
import {
  calculateDistance,
  calculateDistanceForSpecificItem,
} from '@/utils/storeLocator';
import { LOCATION_PERMISSION_STATE } from '@/constants/types';
import {
  fetchStoreLocator,
  fetchSpecificStoreLocator,
  fetchStoreLocatorProduct,
  fetchSpecificStoreLocatorProduct,
  fetchNearbyStores,
} from '../thunk/store-locator-thunk';

const initialState = {
  items: [],
  filteredItems: [],
  total: 0,
  isLoading: false,
  error: null,
  page: process.env.NEXT_PUBLIC_PAGING,
  limit: process.env.NEXT_PUBLIC_FETCH_LIMIT,
  showOnMap: null,
  userLocation: null,
  locPermissionState: LOCATION_PERMISSION_STATE.PROMPT,

  // for in store pickup
  selectedStore: null,
  nearbyStores: [],
  isOpenInStorePickupModal: false,
  selectedDetailsStore: null,
};

const storeLocatorSlice = createSlice({
  name: 'storeLocator',
  initialState,
  reducers: {
    reset: () => initialState,

    setShowOnMap: (state, action) => {
      state.showOnMap = action.payload;
    },
    setUserLocation: (state, action) => {
      state.userLocation = action.payload;
    },
    setLocPermissionState: (state, action) => {
      state.locPermissionState = action.payload;
    },
    setFilteredItems: (state, action) => {
      state.filteredItems = action.payload;
    },
    setItems: (state, action) => {
      state.items = action.payload;
      state.filteredItems = action.payload;
    },

    // in store pickup
    updateInStorePickupModal: (state, action) => ({
      ...state,
      isOpenInStorePickupModal: action.payload,
      // reset previous filtered items to all items
      filteredItems: action.payload ? state.filteredItems : state.items,
    }),
    updateSelectStore: (state, action) => ({
      ...state,
      selectedStore: action.payload,
      selectedDetailsStore: null,
      showOnMap: action.payload,
    }),
    updateSelectDetailsStore: (state, action) => ({
      ...state,
      selectedDetailsStore: action.payload,
      showOnMap: action.payload,
    }),
    backStore: (state) => {
      if (state.selectedDetailsStore) {
        state.selectedDetailsStore = null;
        state.showOnMap = state.selectedStore;
      } else {
        state.isOpenInStorePickupModal = false;
        // reset previous filtered items to all items
        state.filteredItems = state.items;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStoreLocator.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStoreLocator.fulfilled, (state, action) => {
        state.items = calculateDistance(
          action.payload,
          state.locPermissionState,
          JSON.parse(JSON.stringify(state.userLocation)),
        );
        state.filteredItems = calculateDistance(
          action.payload,
          state.locPermissionState,
          JSON.parse(JSON.stringify(state.userLocation)),
        );
        state.total = action.payload.length;
        state.isLoading = false;
        state.showOnMap = action.payload?.[0];
      })
      .addCase(fetchStoreLocator.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // FOR NEARBY STORES
      .addCase(fetchNearbyStores.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNearbyStores.fulfilled, (state, action) => {
        state.nearbyStores =
          action?.payload?.length > 0
            ? calculateDistance(
                action.payload,
                state.locPermissionState,
                JSON.parse(JSON.stringify(state.userLocation)),
              )
            : [];
        state.isLoading = false;
        state.selectedStore =
          action?.payload?.length > 0 ? action?.payload[0] : null;
        state.showOnMap =
          action?.payload?.length > 0 ? action?.payload[0] : null;
        state.selectedDetailsStore = null;

        // reset previous filtered items to all items
        state.filteredItems = state.items;
      })
      .addCase(fetchNearbyStores.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.selectedStore = null;
        state.showOnMap = null;
        state.selectedDetailsStore = null;
      })
      // FOR SPECIFIC STORE
      .addCase(fetchSpecificStoreLocator.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSpecificStoreLocator.fulfilled, (state, action) => {
        state.isLoading = false;
        state.showOnMap = calculateDistanceForSpecificItem(
          action.payload,
          state.locPermissionState,
          state.userLocation,
        );
      })
      .addCase(fetchSpecificStoreLocator.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // GET ALL STORE FOR SPECIFIC PRODUCT
      .addCase(fetchStoreLocatorProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStoreLocatorProduct.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.items = calculateDistance(
            action.payload,
            state.locPermissionState,
            state.userLocation,
          );
          state.filteredItems = state.items;
          state.total = action.payload.length;
          state.isLoading = false;
          state.showOnMap = action.payload?.[0];
        } else {
          state.items = [];
          state.filteredItems = [];
          state.total = 0;
          state.isLoading = false;
          state.showOnMap = null;
        }
      })
      .addCase(fetchStoreLocatorProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // GET SPECIFIC STORE FOR SPECIFIC PRODUCT
      .addCase(fetchSpecificStoreLocatorProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSpecificStoreLocatorProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.showOnMap = calculateDistanceForSpecificItem(
          action.payload,
          state.locPermissionState,
          state.userLocation,
        );
      })
      .addCase(fetchSpecificStoreLocatorProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  reset,
  setShowOnMap,
  setUserLocation,
  setLocPermissionState,
  setFilteredItems,
  setItems,

  // in store pickup
  updateInStorePickupModal,
  updateSelectStore,
  updateSelectDetailsStore,
  backStore,
} = storeLocatorSlice.actions;

export default storeLocatorSlice.reducer;
